/* import __COLOCATED_TEMPLATE__ from './manage-sections.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
// @ts-ignore
import { trackedReset, dedupeTracked } from 'tracked-toolbox';
import SidebarSection from 'embercom/objects/inbox/sidebar-section';
import type Session from 'embercom/services/session';
import { SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import type SidebarSections from '../sidebar-sections';
import type Conversation from 'embercom/objects/inbox/conversation';

interface Args {
  pinnedSections: SidebarSection[];
  availableSections: SidebarSection[];
  sidebarSectionDetails: SidebarSections['sidebarSectionDetails'];
  onUpdateSections: (sections: SidebarSection[]) => unknown;
  conversation?: Conversation;
}

interface Signature {
  Args: Args;
}

export default class ManageSectionsComponent extends Component<Signature> {
  supportedSidebarSection = SupportedSidebarSection;
  @service declare session: Session;

  @tracked private openerElement: HTMLElement | null = null;
  @tracked private viewportHeight = window.innerHeight;
  @dedupeTracked hoveredSection?: SidebarSection;

  private handleWindowResize = (): void => {
    this.viewportHeight = window.innerHeight;
  };

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    window.addEventListener('resize', this.handleWindowResize);
  }

  override willDestroy(): void {
    super.willDestroy();
    window.removeEventListener('resize', this.handleWindowResize);
  }

  @trackedReset({
    memo: 'args.availableSections',
    update() {
      return [...this.pinnedSections, ...this.unpinnedSections];
    },
  })
  sections: SidebarSection[] = [];

  get pinnedSections() {
    return this.args.availableSections.filter((section) => this.isPinned(section));
  }

  get unpinnedSections() {
    return this.args.availableSections.filter((section) => !this.isPinned(section));
  }

  @action updateSections() {
    this.sections = [...this.pinnedSections, ...this.unpinnedSections];
  }

  @action toggleSection(section: SidebarSection) {
    if (this.isPinned(section)) {
      this.args.onUpdateSections(this.args.pinnedSections.reject((s) => s.isSameSection(section)));
    } else {
      this.args.onUpdateSections([...this.args.pinnedSections, new SidebarSection(section)]);
    }
  }

  @action captureOpener(element: HTMLElement): void {
    this.openerElement = element;
  }

  get popoverContentStyle(): string {
    // With left-end placement, Popper sets menu.bottom = opener.bottom.
    // Constraining max-height to opener.bottom - 4px ensures menu.top >= 4px always.
    if (this.openerElement) {
      let maxHeight = Math.min(
        this.openerElement.getBoundingClientRect().bottom - 4,
        this.viewportHeight - 20,
      );
      return sanitizeHtml(`max-height: ${maxHeight}px;`);
    }
    return sanitizeHtml('max-height: calc(100vh - 20px);');
  }

  @action onHover(section: SidebarSection | undefined) {
    if (this.hoveredSection?.isSameSection(section)) {
      return;
    }

    this.hoveredSection = section;
  }

  private isPinned(section: SidebarSection) {
    return this.args.pinnedSections.any((s) => s.isSameSection(section));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::ManageSections': typeof ManageSectionsComponent;
    'inbox2/conversation-details-sidebar/manage-sections': typeof ManageSectionsComponent;
  }
}
